import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import SEO from "@/components/SEO";

export default function BlogPostPage() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setPost(null);
    setOthers([]);
    api.getBlogPost(slug).then(p => {
      if (ignore) return;
      setPost(p);
      api.getBlogPosts().then(all => {
        if (ignore) return;
        setOthers(all.filter(x => x.slug !== slug).slice(0, 3));
      }).catch(() => {});
    }).catch(() => {}).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [slug]);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com";
  const lx = (field) => field?.[language] || field?.en || "";
  const formatDate = (d) => new Date(d).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return (
    <div className="pt-32 pb-20 min-h-screen">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl animate-pulse">
        <div className="h-6 bg-slate-200 w-32 mb-8" />
        <div className="h-10 bg-slate-200 w-3/4 mb-6" />
        <div className="aspect-[16/9] bg-slate-200 mb-8" />
        <div className="space-y-3"><div className="h-4 bg-slate-200 w-full" /><div className="h-4 bg-slate-200 w-5/6" /></div>
      </div>
    </div>
  );

  if (!post) return (
    <div className="pt-32 pb-20 min-h-screen flex flex-col items-center justify-center gap-6">
      <p className="text-slate-500">Article not found</p>
      <Link to="/blog" className="text-sm uppercase tracking-widest font-medium text-slate-800 hover:text-slate-600">{t("blogPage.backToBlog")}</Link>
    </div>
  );

  return (
    <div data-testid="blog-post-page">
      <SEO
        title={post.title.en}
        description={post.excerpt.en}
        keywords={post.keywords}
        canonical={`${baseUrl}/blog/${post.slug}`}
        type="article"
        image={post.image}
        breadcrumbs={[
          { name: "Home", url: baseUrl },
          { name: "Blog", url: `${baseUrl}/blog` },
          { name: post.title.en, url: `${baseUrl}/blog/${post.slug}` }
        ]}
        article={{
          headline: post.title.en,
          description: post.excerpt.en,
          image: `${baseUrl}${post.image}`,
          datePublished: post.date,
          url: `${baseUrl}/blog/${post.slug}`
        }}
      />

      {/* Header */}
      <section className="relative pt-32 pb-10 md:pt-40 md:pb-12" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <Link to="/blog" data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors duration-200 mb-6">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("blogPage.backToBlog")}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-1 font-medium">{lx(post.category)}</span>
            <span className="flex items-center gap-1 text-xs text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>
              <Clock className="w-3 h-3" /> {post.read_time} {t("blogPage.minRead")}
            </span>
            <span className="text-xs text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>{formatDate(post.date)}</span>
          </div>
          <h1 data-testid="blog-post-title" className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-tight">
            {lx(post.title)}
          </h1>
        </div>
      </section>

      {/* Cover Image */}
      <section className="bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl -mt-0 pt-10">
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.image} alt={lx(post.title)} width="900" height="506" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <p className="text-lg text-slate-600 leading-relaxed mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>
            {lx(post.excerpt)}
          </p>
          {post.sections.map((section, i) => (
            <div key={i} className="mb-10" data-testid={`blog-section-${i}`}>
              <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-4">{lx(section.heading)}</h2>
              <p className="text-base text-slate-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{lx(section.body)}</p>
            </div>
          ))}

          {/* Inline CTA */}
          <div className="mt-12 p-8 bg-slate-50 border border-slate-100 text-center">
            <h3 className="text-xl md:text-2xl font-medium text-slate-900 mb-3">{t("blogPage.ctaTitle")}</h3>
            <p className="text-sm text-slate-500 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>{t("blogPage.ctaText")}</p>
            <Link to="/contact" data-testid="blog-post-cta"
              className="inline-block bg-slate-800 text-white px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
              {t("blogPage.ctaButton")}
            </Link>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {others.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50 border-t border-slate-100">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-10">{t("blogPage.relatedArticles")}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {others.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`}
                  className="group bg-white overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={lx(p.title)} loading="lazy" width="400" height="250" decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs uppercase tracking-wider text-amber-700 font-medium">{lx(p.category)}</span>
                    <h3 className="text-base font-medium text-slate-900 mt-2 leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>{lx(p.title)}</h3>
                    <span className="inline-flex items-center gap-2 text-sm text-slate-800 mt-3 uppercase tracking-widest font-medium group-hover:gap-3 transition-all duration-300">
                      {t("blogPage.readArticle")} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
